<template>
    <div>
        <h1 class="mb-1">Markenstärke 2
            <v-tooltip right>
                <template v-slot:activator="{ on }">
                    <v-icon @click.stop="dialog = true" color="primary" class="ml-2" dark v-on="on">mdi-information-outline</v-icon>
                </template>
                <span>Beschreibung</span>
            </v-tooltip>
        </h1>
        <div class="mb-5 grey--text text--darken-1 font-regular">Wie oft wird Ihr Brand pro Monat auf Google gesucht?</div>

        <v-dialog v-model="dialog" max-width="500">
            <v-card>
                <v-card-title class="headline">Markenstärke</v-card-title>
                <v-card-text>
                    <p>Dieser Wert zeigt an, wie oft <strong>Ihre Marke</strong> pro Monat bei Google gesucht wurde.<br>Sie können daraus direkt ableiten, welche Marken-Nachfrage Ihre TV-, Plakat- und anderen Branding Aktivitäten erzielten.</p>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="green darken-1" text @click="dialog = false">OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div class="product-small" v-if="preview">
            <v-progress-linear :indeterminate="true" v-if="loading" />
            <apex-line-chart :x="volume.chart.x" :y="volume.chart.y" height="400" :format="price" :tooltip="tooltip" v-if="!loading" />
        </div>

        <div class="product-full" v-if="!preview">
            <v-progress-linear :indeterminate="true" v-if="loading" />

            <v-card v-if="!loading">
                <v-card-text>
                    <p class="subtitle-1 font-weight-regular">Dieser Wert zeigt an, wie oft <strong>Ihre Marke</strong> pro Monat bei Google gesucht wurde.<br>Sie können daraus direkt ableiten, welche Marken-Nachfrage Ihre TV-, Plakat- und anderen Branding Aktivitäten erzielten.</p>

                    <v-data-table :hide-default-footer="true" :headers="volume.table.header" :items="volume.table.body" class="elevation-1" mobile-breakpoint="0">
                        <template v-slot:item="{ item }">
                            <tr>
                                <td v-for="(c, i) in volume.table.header" :key="i" :align="i ? 'right' : 'left'" style="padding:8px">
                                    <div v-if="c.value == 'domain'" class="d-flex flex-column">
                                        <div class="branded-table-title">{{ item.domain }}</div>
                                        <div class="branded-table-subtitle">Markennachfrage</div>
                                        <div class="branded-table-subtitle">Wachstum</div>
                                    </div>
                                    <div v-if="c.month" class="d-flex flex-column">
                                        <div class="branded-table-title"></div>
                                        <div>
                                            {{ price(item[c.value].volume) }}
                                        </div>
                                        <div>
                                            <small>
                                                <growth :value="item[c.value].growth" />
                                            </small>
                                        </div>
                                    </div>
                                    <div v-if="c.value == 'total'" class="d-flex justify-center font-weight-bold subtitle-1">
                                        <div class="branded-table-title"></div>
                                        <div>
                                            <growth :value="item.growth" />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>

            <v-row v-if="!loading">
                <v-col cols="6">
                    <v-card class="mt-10">
                        <v-card-text>
                            <apex-line-chart :x="volume.chart.x" :y="volume.chart.y" height="400" :format="price" :tooltip="tooltip" v-if="!loading" />
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="6">
                    <v-card class="mt-10">
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <v-col cols="12" lg="6">
                                        <v-data-table :hide-default-footer="true" :headers="share.table.header" :items="share.table.body" class="elevation-1 non-branded-table" mobile-breakpoint="0">
                                            <template v-slot:item="{ item }">
                                                <tr>
                                                    <td>{{ item.domain }}</td>
                                                    <td class="text-right">{{ item.year.share }}%</td>
                                                    <td class="text-right">
                                                        <growth :value="item.year.growth" />
                                                    </td>
                                                </tr>
                                            </template>
                                        </v-data-table>
                                    </v-col>
                                    <v-col cols="12" lg="6">
                                        <apex-donut-chart v-if="!loading" :x="share.chart.x" :y="share.chart.y" :format="price" :tooltip="price" />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<style scoped>
    .branded-table-title {
        font-weight: bold;
        height: 1.5em;
        font-size: 1.2em;
    }
    .branded-table-subtitle {
        opacity: .5;
        font-size: .9em;
    }
</style>

<script>
import ApexLineChart from "@/components/charts/ApexLineChart";
import ApexDonutChart from "@/components/charts/ApexDonutChart";
import Growth from "@/components/common/Growth";
import ProductMixin from "@/mixins/Product";
import { headers } from "@/helpers/table";
import moment from "moment";
import { price } from "@/helpers/format";

export default {
    data: () => ({
        dialog: false,
        volume: {
            table: { header: [], body: [] },
            chart: { x: [], y: [] }
        },
        share: {
            table: { header: [], body: [] },
            chart: { x: [], y: [] }
        }
    }),
    mixins: [ProductMixin],
    methods: {
        price,
        tooltip(value, growth) {
            let s = price(value);

            if (growth === null)
                s += ` ∞`;
            else if (growth < 0)
                s += ` <span class="red--text">${growth}%</span>`;
            else if (growth > 0)
                s += ` <span class="green--text">+${growth}%</span>`;
            else
                s += ` 0%`;

            return s;
        },
        async loaded(data) {
            this.volume.table.header = headers([
                { text: 'Domain', value: 'domain' },
                ...data.months.map(m => ({
                    text: moment(m).format("MMM YY"),
                    value: m,
                    align: 'right',
                    month: true
                }))
            ]);

            this.volume.table.body = data.domains.map(d => Object.assign({
                    domain: d.domain,
                    growth: d.lastMonth.growth
                },
                ...d.months.map(m => ({
                    [m.date]: m
                }))
            ));

            if (this.mode !== "preview") {
                this.volume.table.header.push({
                    text: "Wachstum über 12 Monate",
                    align: 'center',
                    class: 'primary white--text',
                    sortable: false,
                    value: 'total',
                    width: '120',
                });
            }

            this.volume.chart.x = data.months.map(d => moment(d).format("MMM YY"));
            this.volume.chart.y = data.domains.map(d => ({
                name: d.domain,
                data: d.months.map(m => m.volume),
                tooltip: d.months.map(m => m.growth)
            }));

            this.share.chart.x = data.domains.map(d => d.domain);
            this.share.chart.y = data.domains.map(d => d.year.volume);

            this.share.table.header = headers([
                { text: 'Domain' },
                { text: 'Marktanteil', align: 'right' },
                { text: 'Vgl. Vorjahr', align: 'right' },
            ]);
            this.share.table.body = data.domains;

            return Promise.resolve();
        }
    },
    components: { Growth, ApexLineChart, ApexDonutChart }
}
</script>